import { useMutation, useQuery } from '@apollo/client'
import { useTheme } from '@emotion/react'
import {
    Button,
    CircularProgress,
    FormControlLabel,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material'
import { JobBoardNotInterestedReasons } from '__generated__/graphql'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import {
    GetCandidateJobBoard,
    RespondToJobBoard,
} from 'candidate/graphql/jobBoard.gql'
import { candidateRouteConfig } from 'candidate/routes'
import { useFormik } from 'formik'
import { FC, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import useToast from 'shared/components/Toast/useToast'
import { commonRouteConfig } from 'shared/routeConfig'
import { getPxAsRem } from 'shared/utils'
import { getToken, setToken } from 'shared/utils/auth'
import * as Yup from 'yup'
import InterestedFormFields from './InterestedFormFields'
import NotInterestedFormFields from './NotInterestedFormFields'

interface JobBoardProps {}

export interface JobBoardFormValues {
    interested?: boolean | null
    sectorIds: string[]
    locationIds: string[]
    notInterestedComments?: string
    interestedComments?: string
    notInterestedReason?: JobBoardNotInterestedReasons | null
    disabled: boolean
}

const validationSchema: Yup.ObjectSchema<JobBoardFormValues> = Yup.object({
    interested: Yup.boolean().nullable(),
    interestedComments: Yup.string().optional(),
    notInterestedComments: Yup.string().optional(),
    sectorIds: Yup.array()
        .required()
        .of(Yup.string().required())
        .when('interested', {
            is: true,
            then: (schema) => schema.min(1, 'Sector preferences are required'),
        }),
    locationIds: Yup.array()
        .required()
        .of(Yup.string().required())
        .when('interested', {
            is: true,
            then: (schema) =>
                schema.min(1, 'Location preferences are required'),
        }),

    notInterestedReason: Yup.mixed<JobBoardNotInterestedReasons>()
        .required('Please select a reason')
        .oneOf(Object.values(JobBoardNotInterestedReasons))
        .when('interested', {
            is: true,
            then: (schema) => schema.notRequired(),
        }),
    disabled: Yup.boolean().required(),
})

const JobBoard: FC<JobBoardProps> = () => {
    usePageTitle('Job Board')
    const toast = useToast()
    const theme = useTheme()
    const navigate = useNavigate()
    const { jobBoardId } = useParams<{
        jobBoardId: string
    }>()
    const [searchParams] = useSearchParams()
    const existingToken = getToken()
    const newToken = searchParams.get('token')

    if (!existingToken && !!newToken) {
        setToken(newToken)
    }

    const { data, refetch, loading } = useQuery(GetCandidateJobBoard, {
        variables: {
            jobBoardId: jobBoardId || '',
        },
        onError: (error) => {
            navigate(commonRouteConfig.index.path)
            toast({
                title: 'Error',
                message: error.message || 'Something went wrong.',
            })
        },
    })

    const [saveJobBoardResponse] = useMutation(RespondToJobBoard)

    const candidateName =
        data?.getCandidateJobBoard?.candidate?.user?.nickname ||
        data?.getCandidateJobBoard?.candidate?.user?.firstName
    const jobBoardName = data?.getCandidateJobBoard?.jobBoard?.name
    const jobBoardDescription =
        data?.getCandidateJobBoard?.jobBoard?.description
    const companyName = data?.getCandidateJobBoard?.jobBoard?.company?.value
    const sectors = data?.getCandidateJobBoard?.jobBoard?.sectors?.map((s) => (
        <Typography
            color={theme.palette.other.secondary_light}
            variant="body2"
            key={s.sector?.id}
        >
            {s.sector?.value}
        </Typography>
    ))
    const startDate = data?.getCandidateJobBoard?.jobBoard?.startDate
    const locations = data?.getCandidateJobBoard?.jobBoard?.companyOffices?.map(
        (co) => (
            <Typography
                key={co.companyOffice?.id}
                color={theme.palette.other.secondary_light}
                variant="body2"
            >
                {co.companyOffice?.officeLocation?.value}
            </Typography>
        )
    )

    const titles = data?.getCandidateJobBoard?.jobBoard?.titles?.map((t) => (
        <Typography
            color={theme.palette.other.secondary_light}
            variant="body2"
            key={t.title?.id}
        >
            {t.title?.value}
        </Typography>
    ))

    const onSubmit = async () => {
        const sharedVariables = {
            jobBoardId: jobBoardId || '',
            isInterested: formik.values.interested || false,
        }
        const interestedVariables = {
            comments: formik.values.interestedComments,
            companyOfficePrefIds: formik.values.locationIds,
            sectorPrefIds: formik.values.sectorIds,
            ...sharedVariables,
        }
        const notInterestedVariables = {
            comments: formik.values.notInterestedComments,
            notInterestedReason: formik.values.notInterestedReason,
            ...sharedVariables,
        }
        await saveJobBoardResponse({
            variables: formik.values.interested
                ? interestedVariables
                : notInterestedVariables,
            onError: (error) => {
                toast({
                    title: 'Error',
                    message: error.message || 'Something went wrong.',
                })
                console.error('Error', error)
            },
            onCompleted: () => {
                toast({
                    title: 'Success',
                    severity: 'success',
                    message: 'Your response has been submitted.',
                })
                refetch()
            },
        })
    }

    const initialValues: JobBoardFormValues = {
        interested: null,
        sectorIds: [],
        locationIds: [],
        interestedComments: '',
        notInterestedComments: '',
        notInterestedReason: null,
        disabled: false,
    }

    const { setFieldValue, ...formik } = useFormik<JobBoardFormValues>({
        initialValues,
        validationSchema,
        onSubmit,
    })

    useEffect(() => {
        // if the candidate has already responded, show their responses in a disabled state
        if (
            data?.getCandidateJobBoard.isInterested !== undefined &&
            data?.getCandidateJobBoard.isInterested !== null
        ) {
            setFieldValue('disabled', true)
            setFieldValue('interested', data.getCandidateJobBoard.isInterested)
            setFieldValue(
                'sectorIds',
                data.getCandidateJobBoard.sectorPrefs?.map((s) => s.sector?.id)
            )
            setFieldValue(
                'locationIds',
                data.getCandidateJobBoard.officePrefs?.map(
                    (o) => o.companyOfficeId
                )
            )
            setFieldValue(
                'interestedComments',
                data.getCandidateJobBoard.comments
            )
            setFieldValue(
                'notInterestedComments',
                data.getCandidateJobBoard.comments
            )
            setFieldValue(
                'notInterestedReason',
                data.getCandidateJobBoard.notInterestedReason
            )
        }
    }, [data, setFieldValue])

    if (loading)
        return (
            <Stack alignItems="center" justifyContent="center" height="100%">
                <CircularProgress size={64} />
            </Stack>
        )

    return (
        <Stack
            alignItems="flexStart"
            justifyContent="center"
            padding={getPxAsRem(24, 48)}
            gap={3}
        >
            <Typography
                variant="h3"
                component="h1"
                sx={{ fontFamily: 'Roboto Serif' }}
            >
                Hello {candidateName}
            </Typography>
            <Typography variant="body2">{jobBoardName}</Typography>
            <Typography
                variant="body2"
                color={theme.palette.other.secondary_light}
            >
                {jobBoardDescription}
            </Typography>
            <Stack direction="row" justifyContent="space-between">
                <Stack>
                    <Typography variant="body2">Company Name</Typography>
                    <Typography
                        color={theme.palette.other.secondary_light}
                        variant="body2"
                    >
                        {companyName}
                    </Typography>
                </Stack>
                <Stack>
                    <Typography variant="body2">Start Date</Typography>
                    <Typography
                        color={theme.palette.other.secondary_light}
                        variant="body2"
                    >
                        {startDate}
                    </Typography>
                </Stack>
                <Stack>
                    <Typography variant="body2">Titles</Typography>
                    <Typography
                        color={theme.palette.other.secondary_light}
                        variant="body2"
                    >
                        {titles}
                    </Typography>
                </Stack>

                <Stack>
                    <Typography variant="body2">Sectors</Typography>
                    <Typography
                        color={theme.palette.other.secondary_light}
                        variant="body2"
                    >
                        {sectors}
                    </Typography>
                </Stack>
                <Stack>
                    <Typography variant="body2">Locations</Typography>
                    <Typography
                        color={theme.palette.other.secondary_light}
                        variant="body2"
                    >
                        {locations}
                    </Typography>
                </Stack>
            </Stack>
            <form>
                <RadioGroup
                    value={formik.values.interested}
                    row
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-evenly',
                        flexGrow: 1,
                        margin: 3,
                    }}
                >
                    <FormControlLabel
                        disabled={formik.values.disabled}
                        control={
                            <Radio
                                value={true}
                                onClick={() =>
                                    setFieldValue('interested', true)
                                }
                            />
                        }
                        label="Interested"
                        slotProps={{
                            typography: {
                                sx: {
                                    fontSize: getPxAsRem(20),
                                    fontFamily: 'Roboto Serif',
                                    fontWeight: 500,
                                },
                            },
                        }}
                    />
                    <FormControlLabel
                        disabled={formik.values.disabled}
                        control={
                            <Radio
                                value={false}
                                onClick={() =>
                                    setFieldValue('interested', false)
                                }
                            />
                        }
                        label="Not Interested"
                        slotProps={{
                            typography: {
                                sx: {
                                    fontSize: getPxAsRem(20),
                                    fontFamily: 'Roboto Serif',
                                    fontWeight: 500,
                                },
                            },
                        }}
                    />
                </RadioGroup>
                {formik.values.interested && (
                    <InterestedFormFields
                        canEdit={true}
                        formik={{ ...formik, setFieldValue }}
                        jobBoard={data?.getCandidateJobBoard.jobBoard}
                    />
                )}
                {formik.values.interested === false && (
                    <NotInterestedFormFields
                        canEdit={true}
                        formik={{ ...formik, setFieldValue }}
                    />
                )}
            </form>
            {formik.values.disabled && (
                <Stack gap={2}>
                    <Typography variant="h3">Thank you</Typography>
                    <Typography
                        variant="body2"
                        color={theme.palette.other.secondary_light}
                    >
                        Your input has been successfully submitted.
                    </Typography>
                </Stack>
            )}
            {formik.values.interested !== undefined &&
                !formik.values.disabled && (
                    <Button
                        sx={{ marginTop: getPxAsRem(24) }}
                        onClick={() => formik.handleSubmit()}
                    >
                        Submit
                    </Button>
                )}
            {formik.values.disabled && (
                <Button
                    sx={{ marginTop: getPxAsRem(24) }}
                    onClick={() =>
                        navigate(candidateRouteConfig.dashboard.path)
                    }
                >
                    Close
                </Button>
            )}
        </Stack>
    )
}

export default JobBoard
